// OneDrive:CoverageThreshold(79)

import IServerView from '../../interfaces/view/IServerView';
import IVisualization, { VisualizationType } from '../../interfaces/list/IVisualization';
import { IView, ViewType, ViewScope } from '../../interfaces/view/IView';
import IListPermissions from '../../interfaces/list/IListPermissions';
import { IViewArrangeInfo, IOrderedField, IGroupBy, IRowLimit, ArrangeInfoType, IViewDomParts } from '../../interfaces/view/IViewArrangeInfo';
import Guid from '@ms/odsp-utilities/lib/guid/Guid';
import { CamlConstants, CamlTags as Tags, CamlAttributes as Attrs } from '../../utilities/caml/CamlConstants';
import * as CamlParsing from '../../utilities/caml/CamlParsingBasic';
import * as CamlUtilities from '../../utilities/caml/CamlUtilities';
import { Qos as QosEvent, ResultTypeEnum } from '@ms/odsp-utilities/lib/logging/events/Qos.event';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import DomUtils from '@ms/odsp-utilities/lib/domUtils/DomUtils';
import { Killswitch } from '@ms/odsp-utilities/lib/killswitch/Killswitch';

let getSerializer = (): XMLSerializer => {
  const XMLSerializer = window['XMLSerializer'];

  const serializer = new XMLSerializer();

  getSerializer = () => serializer;

  return serializer;
}

export default class View implements IView {
  // Note: when add any new public property, need to make sure
  // update _cloneViewProperties method to ensure the clone
  // method will copy the new property.
  public title: string;
  public id: string;
  public isDefault: boolean;
  public isHidden: boolean;
  public isPersonal: boolean;
  public isReadOnly: boolean;
  public modifiedInEditor: boolean;
  public rowLimit: number;
  public scope: ViewScope;
  public serverRelativeUrl: string;
  public tabularView: boolean;
  public viewType: ViewType;
  public visualizationInfo: IVisualization;
  /** Probably not the property you're looking for... */
  public baseViewId: string;
  public customFormatter: string;

  private _baseViewXml: string;
  private _isDirty: boolean;
  private _hasParseError: boolean;
  private _modificationTypes: ArrangeInfoType[];

  /** Current XML document (generated only if modifications have been made) */
  private _xmlDoc: Document;
  /** Relevant DOM elements from the view (generated only if modifications have been made) */
  private _viewDom: IViewDomParts;

  /**
   * Constructs a view from either an IServerView or an XML string.
   * The XML string constructor is useful if you just want to manipulate view XML.
   * (Although the view ID and other information is often present in the view XML,
   * the constructor makes no effort to pull it out if given a string.)
   */
  constructor(viewDef: IServerView | string) {
    if (typeof viewDef === 'string') {
      this.baseViewXml = viewDef;
      return;
    }

    let view = <IServerView>viewDef;

    // NOTE: If updating what server view fields are used here, also update which ones are
    // requested in ListDataSource.getViews.
    this.id = Guid.normalizeLower(view.Id, false /*includeBrackets*/);
    this.title = view.Title;
    this.isDefault = view.DefaultView;
    this.isHidden = view.Hidden;
    this.isPersonal = !!view.PersonalView;
    this.isReadOnly = view.ReadOnlyView;
    this.modifiedInEditor = view.EditorModified;
    this.rowLimit = view.RowLimit;
    this.scope = view.Scope || ViewScope.defaultScope;
    this.serverRelativeUrl = view.ServerRelativeUrl;
    this.baseViewId = view.BaseViewId;
    this.baseViewXml = view.ListViewXml;
    this.customFormatter = view.CustomFormatter;
    // TODO: Once the server always returns this, it can default to false or undefined
    let {
      TabularView = true
    } = view;
    this.tabularView = TabularView;

    if (View.isAppView(view)) {
      this.isHidden = false;
      this.viewType = ViewType.power;
      this.visualizationInfo = view.VisualizationInfo;
    } else {
      if (view.ViewType) {
        switch (view.ViewType.toLowerCase()) {
          case 'html': this.viewType = ViewType.standard; break;
          case 'grid': this.viewType = ViewType.datasheet; break;
          case 'calendar': this.viewType = ViewType.calendar; break;
          case 'recurrence': this.viewType = ViewType.recurrence; break;
          case 'chart': this.viewType = ViewType.chart; break;
          case 'gantt': this.viewType = ViewType.gantt; break;
        }
      }
    }
  }

  public static isAppView(view: IServerView): boolean {
    return !!(view &&
      view.Hidden &&
      view.VisualizationInfo &&
      view.VisualizationInfo.VisualizationType === VisualizationType.VisualizationApp &&
      view.VisualizationInfo.VisualizationAppInfo &&
      view.VisualizationInfo.VisualizationAppInfo.Id);
  }

  public canUserEdit(permissions: IListPermissions): boolean {
    if (!permissions || this.isReadOnly) {
      return false;
    }
    let personalPermissionsRelevant = permissions.managePersonalViews && this.isPersonal;
    return permissions.manageLists || personalPermissionsRelevant;
  }

  public get baseViewXml(): string {
    return this._baseViewXml;
  }

  /**
   * Set the base view XML and clear previous modifications (if any).
   * This is mainly meant for cases where the view XML was not initially defined.
   */
  public set baseViewXml(newXml: string) {
    this._baseViewXml = newXml;
    this.clearXmlOverrides(); // also updates modificationTypes
  }

  public getEffectiveViewXml(): string {
    const serializer = getSerializer();

    if (this.isDirty() && this._viewDom) {
      return serializer.serializeToString(this._viewDom.view);
    }
    return this._baseViewXml;
  }

  public getEffectiveQueryXml(includeQueryTag: boolean) {
    this._ensureDom();
    const serializer = getSerializer();

    if (!this._viewDom) {
      return undefined;
    }
    if (!this._viewDom.query) {
      return includeQueryTag ? '<Query/>' : '';
    }
    if (includeQueryTag) {
      return serializer.serializeToString(this._viewDom.query);
    }
    let where = this._viewDom.where ? serializer.serializeToString(this._viewDom.where) : '';
    let orderBy = this._viewDom.orderBy ? serializer.serializeToString(this._viewDom.orderBy) : '';
    let groupBy = this._viewDom.groupBy ? serializer.serializeToString(this._viewDom.groupBy) : '';
    return where + orderBy + groupBy;
  }

  public updateColumnWidths(columnWidths: { [column: string]: string }): void {
    this._ensureDom();

    const columnWidthNodes: NodeListOf<Element> = this._viewDom.view.getElementsByTagName('ColumnWidth');
    const columnWidthExists: boolean = columnWidthNodes.length > 0;
    this._viewDom.columnWidth = columnWidthExists ? columnWidthNodes[0] : this._xmlDoc.createElement(Tags.columnWidth);


    for (const column in columnWidths) {
      const width: string = columnWidths[column];

      const matchedColumnInXML = this._viewDom.columnWidth.querySelector(`[Name=${column}]`);

      if (matchedColumnInXML) { // If column already exists, replace its width value
        matchedColumnInXML.setAttribute(Attrs.width, width);
      } else { // Else add column
        const columndWidth = CamlUtilities.getFieldRefDom(this._xmlDoc, { fieldName: column });
        columndWidth.setAttribute(Attrs.width, width);
        this._viewDom.columnWidth.appendChild(columndWidth);
      }
    }

    if (columnWidthExists) {
      this._viewDom.view.replaceChild(this._viewDom.columnWidth, columnWidthNodes[0]);
    } else {
      this._viewDom.view.appendChild(this._viewDom.columnWidth);
    }

    // Always set dirty because this function is only reached if columns have been resized
    this.setIsDirty(true, ArrangeInfoType.columnWidth);
  }

  public updateColumnAggregateType(columnName: string, aggregateType?: string): void {
    this._ensureDom();
    if (!this._viewDom) {
      return;
    }
    let aggregations = this._viewDom.aggregations;
    let view = this._viewDom.view;
    let isModified = false;
    if (aggregations) {
      aggregations.setAttribute(Attrs.value, 'On');
      let columnAggregationNode = aggregations.querySelector(`[${Attrs.name}=${columnName}]`);
      if (columnAggregationNode) {
        if (!aggregateType) {
          aggregations.removeChild(columnAggregationNode);
          if (aggregations.childElementCount === 0) {
            this._viewDom.view.removeChild(aggregations);
            this._viewDom.aggregations = undefined;
          }
        } else {
          columnAggregationNode.setAttribute(Attrs.type, aggregateType);
        }
        isModified = true;
      } else if (aggregateType) {
        let newColumnAggregationNode = this._xmlDoc.createElement(Tags.fieldRef) as Element;
        newColumnAggregationNode.setAttribute(Attrs.name, columnName);
        newColumnAggregationNode.setAttribute(Attrs.type, aggregateType);
        aggregations.appendChild(newColumnAggregationNode);
        isModified = true;
      }
    } else if (aggregateType) {
      aggregations = this._xmlDoc.createElement(Tags.aggregations);
      aggregations.setAttribute(Attrs.value, 'On');
      let newColumnAggregationNode = this._xmlDoc.createElement(Tags.fieldRef) as Element;
      newColumnAggregationNode.setAttribute(Attrs.name, columnName);
      newColumnAggregationNode.setAttribute(Attrs.type, aggregateType);
      aggregations.appendChild(newColumnAggregationNode);
      this._viewDom.aggregations = aggregations;
      view.appendChild(aggregations);
      isModified = true;
    }

    if (isModified) {
      this.setIsDirty(true, ArrangeInfoType.aggregations);
    }
  }

  public replaceAggregates(aggregations?: { [index: string]: string[] }): void {
    this._ensureDom();
    if (!this._viewDom) {
      return;
    }
    if (this._viewDom.aggregations) {
      this._viewDom.view.removeChild(this._viewDom.aggregations);
      this._viewDom.aggregations = undefined;
    }
    let viewDomAggregations = this._viewDom.aggregations;
    if (aggregations) {
      const fieldNames = aggregations[Attrs.name];
      const aggregateTypes = aggregations[Attrs.type];
      if (fieldNames && aggregateTypes && fieldNames.length === aggregateTypes.length) {
        for (let i = 0; i < fieldNames.length; i++) {
          if (!viewDomAggregations) {
            viewDomAggregations = this._xmlDoc.createElement(Tags.aggregations);
            viewDomAggregations.setAttribute(Attrs.value, 'On');
          }
          let newColumnAggregationNode = this._xmlDoc.createElement(Tags.fieldRef) as Element;
          newColumnAggregationNode.setAttribute(Attrs.name, fieldNames[i]);
          newColumnAggregationNode.setAttribute(Attrs.type, aggregateTypes[i]);
          viewDomAggregations.appendChild(newColumnAggregationNode);
        }
      }
    }
    if (viewDomAggregations) {
      this._viewDom.aggregations = viewDomAggregations;
      this._viewDom.view.appendChild(viewDomAggregations);
    }
    this.setIsDirty(true, ArrangeInfoType.aggregations);
  }

  // Update the customFormatter field in view xml
  public updateCustomFormatter(customFormatter: string): void {
    this._ensureDom();

    const formatterNodes: NodeListOf<Element> = this._viewDom.view.getElementsByTagName('CustomFormatter');
    const formatterNodeExists: boolean = formatterNodes.length > 0;
    this._viewDom.customFormatter = formatterNodeExists ? formatterNodes[0] : this._xmlDoc.createElement(Tags.customFormatter);
    if (!Killswitch.isActivated("622E8315-E06C-4D51-93FC-8DDA27AD3C56", "10/17/2018", "KillSwitchUseCDATAForViewFormatter")) {
      // clear up the children and appendCDATA so that we can support <>& chars without issues.
      // server support for CDATA has already been checked in separately.
      this._viewDom.customFormatter.textContent = '';
      if (customFormatter) {
        this._viewDom.customFormatter.appendChild(this._xmlDoc.createCDATASection(customFormatter));
      }
    } else {
      this._viewDom.customFormatter.textContent = customFormatter;
    }

    if (formatterNodeExists) {
      this._viewDom.view.replaceChild(this._viewDom.customFormatter, formatterNodes[0]);
    } else {
      this._viewDom.view.appendChild(this._viewDom.customFormatter);
    }
    this.setIsDirty(true, ArrangeInfoType.customFormatter);
  }

  public updateNewDocumentTemplates(templates: string): void {
    this._ensureDom();

    const templatesNodes: NodeListOf<Element> = this._viewDom.view.getElementsByTagName(Tags.newDocumentTemplates);
    const templatesExists: boolean = templatesNodes.length > 0;
    this._viewDom.newDocumentTemplates = templatesExists ? templatesNodes[0] : this._xmlDoc.createElement(Tags.newDocumentTemplates);
    this._viewDom.newDocumentTemplates.textContent = templates;

    if (templatesExists) {
      this._viewDom.view.replaceChild(this._viewDom.newDocumentTemplates, templatesNodes[0]);
    } else {
      this._viewDom.view.appendChild(this._viewDom.newDocumentTemplates);
    }

    this.setIsDirty(true, ArrangeInfoType.newDocumentTemplates);
  }

  public getEffectiveFieldNames(): string[] {
    this._ensureDom();
    return this._viewDom && this._viewDom.viewFields ? CamlParsing.parseViewFields(this._viewDom.viewFields) : undefined;
  }

  public getEffectiveAggregatesData(): { [index: string]: string[] } | undefined {
    this._ensureDom();
    return this._viewDom && this._viewDom.aggregations ? CamlParsing.parseAggregations(this._viewDom.aggregations) : undefined;
  }

  public getEffectiveAggregatesFieldXml(): string | undefined {
    this._ensureDom();
    return this._viewDom && this._viewDom.aggregations ?
      CamlParsing.parseAggregationsToGetEffectiveAggregatesFieldsXml(this._viewDom.aggregations) : undefined;
  }

  public isDirty(specificModification?: ArrangeInfoType): boolean {
    if (typeof specificModification === 'number') {
      return !!this._modificationTypes && this._modificationTypes.indexOf(specificModification) !== -1;
    }
    return this._isDirty;
  }

  public setIsDirty(isDirty: boolean, modificationType?: ArrangeInfoType) {
    this._isDirty = isDirty;
    if (!isDirty) {
      this._modificationTypes = [];
    } else if (typeof modificationType === 'number' && this._modificationTypes.indexOf(modificationType) === -1) {
      this._modificationTypes.push(modificationType);
    }
  }

  public hasParseError(): boolean {
    return this._hasParseError;
  }

  public getDomParts(): IViewDomParts {
    this._ensureDom();
    return this._viewDom;
  }

  public clearXmlOverrides() {
    this._hasParseError = false;
    this._xmlDoc = undefined;
    this._viewDom = undefined;
    this.setIsDirty(false);
  }

  public prepareForSaving() {
    this._ensureDom();
    if (!this._viewDom || !this._viewDom.where) {
      return;
    }

    let filtersWithId = this._viewDom.where.querySelectorAll('[id]');
    for (let i = 0; i < filtersWithId.length; i++) {
      filtersWithId[i].removeAttribute('id');

      if (!this._isDirty) {
        // Need to set is Dirty to true when we remove the attribute.
        // This is to ensure when call getEffectiveViewXml, it will not just return the baseViewXml.
        this._isDirty = true;
      }
    }
  }

  public updateSort(sort: IOrderedField, options: { removeSort?: boolean; overwriteAll?: boolean; prepend?: boolean } = {}) {
    this._ensureDom();
    let { removeSort, overwriteAll, prepend } = options;
    let removeAll = !sort && overwriteAll;
    // No-op if the view XML is unknown or invalid, the sort is not specified, or we're told
    // to remove a sort when in fact no sorts are present.
    if (!this._viewDom || (!removeAll && !sort) || (removeSort && !this._viewDom.orderBy)) {
      return;
    }

    let orderBy = this._viewDom.orderBy;
    if (removeAll) {
      // delete all sort info if requested
      if (orderBy) {
        this._viewDom.query.removeChild(orderBy);
      }
      this._viewDom.orderBy = undefined;
      this.setIsDirty(true, ArrangeInfoType.sorts);
      return;
    }

    let madeModification = false;
    if (!orderBy && !removeAll) {
      // create order by node if it doesn't exist
      orderBy = this._viewDom.orderBy = this._xmlDoc.createElement(Tags.orderBy);
      this._viewDom.query.appendChild(orderBy);
      madeModification = true;
    }

    // for existing sort, find the current element and object
    let elem = orderBy.querySelector(StringHelper.format(CamlConstants.fieldNameQuery, sort.fieldName));
    if (removeSort) {
      // remove sort if requested, and remove parent element too if applicable
      if (elem) {
        orderBy.removeChild(elem);
        madeModification = true;
        if (!orderBy.childElementCount) {
          this._viewDom.query.removeChild(orderBy);
          this._viewDom.orderBy = undefined;
        }
      }
    } else {
      madeModification = true;
      if (elem) {
        // element exists, so update the sort direction
        CamlUtilities.updateBooleanAttr(elem, Attrs.ascending, sort.isAscending);
      }
      let shouldAdd;
      if (overwriteAll) {
        // to overwrite, remove all children first (OrderBy can have extra attributes
        // we might want to preserve)
        for (let child = orderBy.lastChild; !!child; child = orderBy.lastChild) {
          orderBy.removeChild(child);
        }
        shouldAdd = true;
      } else if (elem) {
        // element exists and we're not overwriting, so no further action
        shouldAdd = false;
      } else {
        // new sort to be added at end
        shouldAdd = true;
      }
      if (shouldAdd) {
        elem = elem || CamlUtilities.getFieldRefDom(this._xmlDoc, sort);
        if (prepend) {
          orderBy.insertBefore(elem, orderBy.firstChild);
        } else {
          orderBy.appendChild(elem);
        }
      }
    }

    if (madeModification) {
      this.setIsDirty(true, ArrangeInfoType.sorts);
    }
  }

  public updateGroupBy(groupBy: IGroupBy) {
    this._ensureDom();
    if (!this._viewDom) {
      return;
    }
    // If groupBy is not given (or no valid group1 is given), remove group by info
    let query = this._viewDom.query;
    let groupElem = this._viewDom.groupBy;
    if (!(groupBy && groupBy.group1 && groupBy.group1.fieldName)) {
      if (groupElem) {
        query.removeChild(groupElem);
        this._viewDom.groupBy = undefined;
        this.setIsDirty(true, ArrangeInfoType.groupBy);
      }
      return;
    }

    let { isCollapsed, group1, group2 } = groupBy;
    if (!groupElem) {
      // Create GroupBy element since it didn't exist previously
      this._viewDom.groupBy = groupElem = this._xmlDoc.createElement(Tags.groupBy);
      query.appendChild(groupElem);
    }
    // TODO [elcraig]: is missing collapse attribute valid?
    CamlUtilities.updateBooleanAttr(groupElem, Attrs.collapse, isCollapsed);

    // Add or update grouping info
    let firstGroupElem: Element;
    let secondGroupElem: Element;
    if (groupElem.childElementCount > 0) {
      firstGroupElem = groupElem.firstElementChild;
      secondGroupElem = firstGroupElem.nextElementSibling;
    }

    // Update or replace the first grouping level
    CamlUtilities.replaceField(this._xmlDoc, group1, firstGroupElem, groupElem);

    if (group2 && group2.fieldName && group2.fieldName !== group1.fieldName) {
      // Update or replace the second grouping level
      CamlUtilities.replaceField(this._xmlDoc, group2, secondGroupElem, groupElem);
    } else if (secondGroupElem) {
      // Second group is no longer defined, so remove it
      groupElem.removeChild(secondGroupElem);
    } // else, no old or new second-level group info

    this.setIsDirty(true, ArrangeInfoType.groupBy);
  }

  public updateGroupByCollapseAttribute(isCollapsed: boolean) {
    this._ensureDom();
    if (!this._viewDom) {
      return;
    }

    let groupElem = this._viewDom.groupBy;
    if (groupElem) {
      CamlUtilities.updateBooleanAttr(groupElem, Attrs.collapse, isCollapsed);
    }

    this.setIsDirty(true, ArrangeInfoType.groupBy);
  }

  public updateField(name: string, index?: number) {
    this._ensureDom();
    // No-op if the view XML is unknown/invalid or no field name is given
    if (!this._viewDom || !name) {
      return;
    }

    // The ViewFields tag is pre-created when initializing the view (if it didn't exist in
    // the original XML).
    let madeModification = false;
    let viewFields = this._viewDom.viewFields;
    let elem = viewFields.querySelector(StringHelper.format(CamlConstants.fieldNameQuery, name));

    // If the field exists, remove it (will be added back later if updating)
    if (elem) {
      madeModification = true;
      viewFields.removeChild(elem);
    }

    // Now insert or re-insert the field if applicable
    if (typeof index === 'number') {
      madeModification = true;
      if (!elem) { // create element if it doesn't exist
        elem = CamlUtilities.getFieldRefDom(this._xmlDoc, { fieldName: name });
      }
      if (index >= viewFields.childElementCount) {
        // insert field at end of view fields list
        viewFields.appendChild(elem);
      } else if (index >= 0) {
        // insert field at new index
        DomUtils.insertAtIndex(viewFields, elem, index, Tags.fieldRef);
      }
      // else, field will be removed
    }
    if (madeModification) {
      this.setIsDirty(true, ArrangeInfoType.fieldNames);
    }
  }

  public replaceFields(fieldNames: string[], lookupIds?: (boolean | undefined)[]) {
    this._ensureDom();
    // No-op if the XML is unknown/invalid or no field names are given
    // (removing all fields is not allowed)
    if (!this._viewDom || !fieldNames) {
      return;
    }
    fieldNames = fieldNames.filter((name: string) => !!name);
    if (!fieldNames.length) {
      return;
    }

    let fieldDict: { [name: string]: { index: number; elem?: Element; lookupId?: boolean } } = {};
    for (let i = 0; i < fieldNames.length; i++) {
      // Map from field name to index we want that field at
      fieldDict[fieldNames[i]] = { index: i };
      if (lookupIds && typeof lookupIds[i] === 'boolean') {
        fieldDict[fieldNames[i]].lookupId = lookupIds[i];
      }
    }

    // Walk through the existing view fields and save any we're interested in.
    // With this approach, we'll retain any extra attributes present on the original tags.
    let oldViewFields = this._viewDom.viewFields;
    for (let fieldElem = oldViewFields.firstElementChild; !!fieldElem; fieldElem = fieldElem.nextElementSibling) {
      let fieldName = CamlUtilities.getAttr(fieldElem, Attrs.name);
      if (fieldDict[fieldName]) {
        fieldDict[fieldName].elem = fieldElem;
      }
    }

    // Replace the old ViewFields tag with a new, empty one
    let newViewFields = this._viewDom.viewFields = this._xmlDoc.createElement(Tags.viewFields);
    this._viewDom.view.replaceChild(newViewFields, oldViewFields);
    // Make an array of FieldRef elements in the desired order
    let newFieldElems: Element[] = [];
    for (let fieldName of Object.keys(fieldDict)) {
      let dictEntry = fieldDict[fieldName];
      let fieldElem = dictEntry.elem;
      if (!fieldElem) {
        // Create fields that didn't exist before
        // TODO [elcraig]: are there ever required attributes for certain field types?
        fieldElem = CamlUtilities.getFieldRefDom(this._xmlDoc, { fieldName: fieldName, lookupId: dictEntry.lookupId });
      }
      newFieldElems[dictEntry.index] = fieldElem;
    }

    // Append all the FieldRef elements to the ViewFields tag
    for (let fieldElem of newFieldElems) {
      newViewFields.appendChild(fieldElem);
    }

    this.setIsDirty(true, ArrangeInfoType.fieldNames);
  }

  public clearFilters() {
    this._ensureDom();
    if (this._viewDom.where) {
      this._viewDom.query.removeChild(this._viewDom.where);
      this._viewDom.where = undefined;
      this.setIsDirty(true, ArrangeInfoType.filters);
    }
  }

  public addFilters(camlFilters: string[]) {
    this._ensureDom();
    if (!this._viewDom || !camlFilters) {
      return;
    }
    camlFilters = camlFilters.filter((filter: string) => !!filter);
    if (!camlFilters.length) {
      return;
    }

    let caml = CamlUtilities.combineFilters(camlFilters, Tags.and);
    if (!caml) {
      throw new Error('Invalid filter: must be defined and have values');
    }

    this.setIsDirty(true, ArrangeInfoType.filters);
    let filterElem;
    try {
      filterElem = CamlUtilities.xmlToDom(caml).documentElement;
    } catch (ex) {
      throw new Error('Generated filter was not valid XML!');
    }
    let where = this._viewDom.where;
    if (!where) {
      where = this._viewDom.where = this._xmlDoc.createElement(Tags.where);
      this._viewDom.query.appendChild(where);
    }
    if (!where.childElementCount) {
      // Easy case: no previous filters
      where.appendChild(filterElem);
    } else {
      // Complicated case: And the new filters with the old filters
      let oldFilters = where.firstElementChild;
      let and = this._xmlDoc.createElement(Tags.and);

      // Put the old filters first: for large lists only, the order matters because the
      // first column in the query must be indexed.
      // (Once we append the oldFilters to 'and' element, it is auto removed from its parent.
      // So we only need to append the new 'and' element to 'where'.)
      and.appendChild(oldFilters);
      and.appendChild(filterElem);
      where.appendChild(and);
    }
  }

  public updateRowLimit(rowLimit: IRowLimit) {
    this._ensureDom();
    if (!this._viewDom || !rowLimit || typeof rowLimit.rowLimit !== 'number') {
      return;
    }

    this.rowLimit = rowLimit.rowLimit;

    let rowLimitElem = this._viewDom.rowLimit;
    if (!rowLimitElem) {
      this._viewDom.rowLimit = rowLimitElem = this._xmlDoc.createElement(Tags.rowLimit);
      this._viewDom.view.appendChild(rowLimitElem);
    }

    if (rowLimitElem.firstChild) {
      rowLimitElem.firstChild.nodeValue = String(rowLimit.rowLimit);
    } else {
      let newText = this._xmlDoc.createTextNode(String(rowLimit.rowLimit));
      rowLimitElem.appendChild(newText);
    }

    if (typeof rowLimit.isPerPage === 'boolean') {
      CamlUtilities.updateBooleanAttr(rowLimitElem, Attrs.paged, rowLimit.isPerPage);
    }

    this.setIsDirty(true, ArrangeInfoType.rowLimit);
  }

  public updateAll(arrangeInfo: IViewArrangeInfo) {
    // other methods will call _ensureDom() if needed
    let { fieldNames, groupBy, sorts, filters } = arrangeInfo;
    if (fieldNames && fieldNames.length) {
      this.replaceFields(fieldNames);
    }
    if (groupBy) {
      this.updateGroupBy(groupBy);
    }
    if (sorts && sorts.length) {
      this.updateSort(sorts[0], { overwriteAll: true });
      for (let sort of sorts.slice(1)) {
        this.updateSort(sort);
      }
    }
    if (filters && filters.length && typeof filters[0] === 'string') {
      // TODO [elcraig]: enable IFilters too
      this.clearFilters();
      this.addFilters(<string[]>filters);
    }
  }

  public compareTo(view: IView): number {
    if (!view) {
      return 1;
    }

    // Powerapp views go after non-powerapp views
    if (this.viewType === ViewType.power && view.viewType !== ViewType.power) {
      return 1;
    } else if (this.viewType !== ViewType.power && view.viewType === ViewType.power) {
      return -1;
    }

    // Neither is a powerapp or both are. Compare titles.
    let t1 = (this.title || '').toLocaleLowerCase();
    let t2 = (view.title || '').toLocaleLowerCase();
    return t1 === t2 ? 0 : (t1 < t2 ? -1 : 1);
  }

  public clone(): IView {
    let newView: View;

    if (this.isDirty()) {
      // When view is dirty, we need to make sure cloned view has view Dom that reflect modified view.
      // We also need to make sure it has the isDirty flag to be true
      // and it has the same baseViewXml as original view.
      newView = new View(this.getEffectiveViewXml());
      newView._ensureDom();
      newView.setIsDirty(true);
      newView._modificationTypes = [...this._modificationTypes];
      newView._baseViewXml = this.baseViewXml;
    } else {
      newView = new View(this.baseViewXml);
    }

    this._cloneViewProperties(newView);
    return newView;
  }

  /**
   * Internal private function to update public properties in the cloned view.
   * This should be only used in the clone function above.
   *
   * NOTE: Should not directly update baseViewXml in this function to avoid
   * clearXmlOverrides in the cloned view.
   */
  private _cloneViewProperties(clonedView: IView) {
    clonedView.id = this.id;
    clonedView.title = this.title;
    clonedView.isDefault = this.isDefault;
    clonedView.isHidden = this.isHidden;
    clonedView.isPersonal = this.isPersonal;
    clonedView.isReadOnly = this.isReadOnly;
    clonedView.modifiedInEditor = this.modifiedInEditor;
    clonedView.rowLimit = this.rowLimit;
    clonedView.scope = this.scope;
    clonedView.serverRelativeUrl = this.serverRelativeUrl;
    clonedView.baseViewId = this.baseViewId;
    clonedView.customFormatter = this.customFormatter;
    clonedView.tabularView = this.tabularView;
    clonedView.viewType = this.viewType;
    clonedView.visualizationInfo = this.visualizationInfo;
  }

  private _ensureDom() {
    if (this._baseViewXml && !this._viewDom) {
      this._isDirty = false;
      this._hasParseError = false;

      let qos = new QosEvent({ name: 'View.Parse' });
      try {
        // This will throw if certain invalid stuff is found (see doc comment).
        // It will also ensure that the ViewFields and Query tags exist.
        this._viewDom = CamlParsing.getViewDomParts(this._baseViewXml);
        this._xmlDoc = this._viewDom.xmlDoc;
        qos.end({ resultType: ResultTypeEnum.Success });
      } catch (ex) {
        this._viewDom = undefined;
        this._xmlDoc = undefined;
        this._hasParseError = true;
        qos.end({
          resultType: ResultTypeEnum.Failure,
          error: ex.message || JSON.stringify(ex)
        });
      }
    }
  }
}
